import type { ArvoDocCategory } from './paths';

/** One page of the Eevo docs: Markdown, so the docs page and the `.md` copies for agents are the same text. */
export interface ArvoDocPage {
  category: ArvoDocCategory;
  slug: string;
  title: string;
  /** One sentence: the category listings, the rail's hover text, llms.txt. */
  summary: string;
  /** The body, without the title (the page and the `.md` copy add it). Headings start at `##`. */
  markdown: string;
  /** A page listed under another page of its category (the API reference's endpoints): that page's slug. */
  parent?: string;
  /**
   * The OpenAPI operations the page renders under its text, by `operationId`; a body with variants
   * (`createDecision`, one per mode) renders each of them.
   */
  operationIds?: string[];
}

export const json = (value: unknown) => JSON.stringify(value, null, 2);

/** A fenced code block. */
export function code(language: string, text: string): string {
  return `\`\`\`${language}\n${text}\n\`\`\``;
}

/** The environment variable the docs keep the API key in. */
export const EEVO_API_KEY_VARIABLE = 'EEVO_API_KEY';

/** A curl call with the API key from `$EEVO_API_KEY`, as a shell code block; a body makes it a POST. */
export function curl(url: string, body?: unknown): string {
  const lines = [`curl ${url}${body === undefined ? '' : ' \\'}`];
  if (body !== undefined) {
    lines.push(
      `  -H "Authorization: Bearer $${EEVO_API_KEY_VARIABLE}" \\`,
      '  -H "Content-Type: application/json" \\',
      `  -d '${json(body).replace(/'/g, `'\\''`)}'`,
    );
  } else {
    lines[0] += ' \\';
    lines.push(`  -H "Authorization: Bearer $${EEVO_API_KEY_VARIABLE}"`);
  }
  return code('shell', lines.join('\n'));
}

/**
 * A note set apart from the text around it: what a tutorial is about, before its steps. Written as
 * a GitHub alert (`> [!NOTE]`), which Markdown readers show as a note and the docs page as a
 * coloured box (`arvoDocSegments`).
 */
export function callout(markdown: string): string {
  return ['> [!NOTE]', ...markdown.split('\n').map(line => (line ? `> ${line}` : '>'))].join('\n');
}

const CALLOUT = /^> \[!NOTE\]\n((?:>.*(?:\n|$))*)/gm;

/** A page body in order: plain Markdown, and the callouts between it, each without its `>` prefix. */
export function arvoDocSegments(markdown: string): Array<{ kind: 'markdown' | 'callout'; markdown: string }> {
  const segments: Array<{ kind: 'markdown' | 'callout'; markdown: string }> = [];
  let last = 0;
  for (const match of markdown.matchAll(CALLOUT)) {
    const before = markdown.slice(last, match.index).trim();
    if (before) segments.push({ kind: 'markdown', markdown: before });
    const body = match[1]
      .split('\n')
      .map(line => line.replace(/^> ?/, ''))
      .join('\n')
      .trim();
    segments.push({ kind: 'callout', markdown: body });
    last = match.index + match[0].length;
  }
  const rest = markdown.slice(last).trim();
  if (rest) segments.push({ kind: 'markdown', markdown: rest });
  return segments;
}
